import Box from '@mui/material/Box';
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { pxToRem } from '../../../utils/pxToRem';

const CARD_WIDTH = 160;
const GAP = 32;
const STEP = CARD_WIDTH + GAP;

interface SpeakerCarouselProps<T> {
  items: readonly T[];
  renderItem: (item: T) => ReactNode;
  ariaLabel: string;
}

// Infinite carousel of fixed 160px cards (speakers, diplomats)
export function SpeakerCarousel<T>({ items, renderItem, ariaLabel }: SpeakerCarouselProps<T>) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const copyWidth = items.length * STEP;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = copyWidth;

    const wrap = () => {
      const left = el.scrollLeft;
      if (left < copyWidth) {
        el.scrollLeft = left + copyWidth;
      } else if (left >= 2 * copyWidth) {
        el.scrollLeft = left - copyWidth;
      }
    };

    if (typeof (el as HTMLDivElement & { onscrollend?: unknown }).onscrollend !== 'undefined') {
      el.addEventListener('scrollend', wrap);
      return () => el.removeEventListener('scrollend', wrap);
    }

    let timeoutId: number | null = null;
    const handleScroll = () => {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(wrap, 120);
    };
    el.addEventListener('scroll', handleScroll);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [copyWidth]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const el = scrollRef.current;
    if (!el) return;
    event.preventDefault();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({
      left: event.key === 'ArrowRight' ? STEP : -STEP,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const tripled = [...items, ...items, ...items];

  return (
    <Box
      ref={scrollRef}
      onKeyDown={handleKeyDown}
      role='region'
      aria-label={ariaLabel}
      tabIndex={0}
      sx={{
        display: 'flex',
        gap: pxToRem(GAP),
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        overscrollBehaviorX: 'contain',
        scrollSnapType: 'x mandatory',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        paddingInline: `calc(50% - ${pxToRem(CARD_WIDTH / 2)})`,
        paddingBlock: pxToRem(4),
        '&::-webkit-scrollbar': { display: 'none' },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'secondary.main',
          outlineOffset: '2px',
          borderRadius: pxToRem(20),
        },
      }}
    >
      {tripled.map((item, index) => {
        // Only the middle copy is exposed to assistive tech; the outer two exist for the infinite loop
        const isClone = index < items.length || index >= 2 * items.length;
        return (
          <Box
            key={index}
            aria-hidden={isClone || undefined}
            inert={isClone || undefined}
            sx={{
              flexShrink: 0,
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
            }}
          >
            {renderItem(item)}
          </Box>
        );
      })}
    </Box>
  );
}
